<acs-modal
  [isOpen]="isOpen"
  title="Upload Documents"
  size="large"
  (closed)="onModalClose()"
  (backdropClick)="onModalClose()">
  
  <div class="document-upload-modal">
    
    <!-- Progress Steps -->
    <div class="document-upload-modal__progress">
      <div class="document-upload-modal__steps">
        <div 
          *ngFor="let step of [1,2,3]; let i = index"
          class="document-upload-modal__step"
          [class.document-upload-modal__step--active]="currentStep === step"
          [class.document-upload-modal__step--completed]="currentStep > step"
          (click)="goToStep(step)">
          <div class="document-upload-modal__step-number">{{ step }}</div>
          <div class="document-upload-modal__step-label">
            {{ step === 1 ? 'Files' : step === 2 ? 'Settings' : 'Review' }}
          </div>
        </div>
      </div>
      <div class="document-upload-modal__progress-bar">
        <div 
          class="document-upload-modal__progress-fill"
          [style.width.%]="(currentStep / totalSteps) * 100">
        </div>
      </div>
    </div>

    <!-- Step 1: File Selection -->
    <div *ngIf="currentStep === 1" class="document-upload-modal__step-content">
      <div class="document-upload-modal__section">
        <h3 class="document-upload-modal__section-title">📁 Select Documents</h3>
        
        <!-- File Upload Area -->
        <div 
          class="document-upload-modal__drop-zone"
          [class.document-upload-modal__drop-zone--active]="isDragOver"
          (dragover)="onDragOver($event)"
          (dragleave)="onDragLeave($event)"
          (drop)="onDrop($event)"
          (click)="openFileDialog()">
          
          <input 
            #fileInput
            type="file" 
            multiple
            style="display: none;"
            [accept]="allowedFileTypes.join(',')"
            (change)="onFileSelect($event)">
          
          <div class="document-upload-modal__drop-content">
            <div class="document-upload-modal__drop-icon">📁</div>
            <div class="document-upload-modal__drop-title">
              Drag & Drop Files Here
            </div>
            <div class="document-upload-modal__drop-subtitle">
              or click to browse files
            </div>
            <div class="document-upload-modal__drop-info">
              Supported formats: PDF, Word, Excel, PowerPoint, Images, Text<br>
              Maximum file size: {{ formatFileSize(maxFileSize) }} • Maximum {{ maxTotalFiles }} files
            </div>
          </div>
        </div>

        <!-- Selected Files List -->
        <div *ngIf="uploadData.files.length > 0" class="document-upload-modal__file-list">
          <div class="document-upload-modal__file-list-header">
            <h4>Selected Files ({{ uploadData.files.length }}/{{ maxTotalFiles }})</h4>
            <div class="document-upload-modal__file-list-info">
              Total size: {{ formatFileSize(getTotalFileSize()) }}
            </div>
          </div>
          
          <div class="document-upload-modal__files">
            <div 
              *ngFor="let file of uploadData.files"
              class="document-upload-modal__file-item">
              
              <div class="document-upload-modal__file-icon">
                {{ getFileIcon(file) }}
              </div>
              
              <div class="document-upload-modal__file-info">
                <div class="document-upload-modal__file-name">{{ file.name }}</div>
                <div class="document-upload-modal__file-details">
                  {{ formatFileSize(file.size) }} • {{ file.type }}
                </div>
                
                <!-- Progress Bar (if uploading) -->
                <div 
                  *ngIf="file.status === 'uploading'"
                  class="document-upload-modal__file-progress">
                  <div class="document-upload-modal__progress-bar">
                    <div 
                      class="document-upload-modal__progress-fill"
                      [style.width.%]="file.progress">
                    </div>
                  </div>
                  <span class="document-upload-modal__progress-text">{{ file.progress.toFixed(0) }}%</span>
                </div>
                
                <!-- Error Message -->
                <div 
                  *ngIf="file.status === 'error'"
                  class="document-upload-modal__file-error">
                  ❌ {{ file.errorMessage }}
                </div>
              </div>
              
              <div class="document-upload-modal__file-status">
                <span 
                  *ngIf="file.status === 'completed'"
                  class="document-upload-modal__status-success">✅</span>
                <span 
                  *ngIf="file.status === 'error'"
                  class="document-upload-modal__status-error">❌</span>
              </div>
              
              <button 
                *ngIf="file.status !== 'uploading'"
                class="document-upload-modal__remove-btn"
                (click)="removeFile(file.id)"
                title="Remove file">
                ✕
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Step 2: Settings & Configuration -->
    <div *ngIf="currentStep === 2" class="document-upload-modal__step-content">
      <div class="document-upload-modal__section">
        <h3 class="document-upload-modal__section-title">⚙️ Document Settings</h3>
        
        <!-- Upload to Personal Repository -->
        <div class="document-upload-modal__field">
          <div class="document-upload-modal__info-box">
            <span class="document-upload-modal__info-icon">👤</span>
            <div>
              <strong>Personal Document Repository</strong>
              <p>Documents will be uploaded to your private repository. Only you can access these files.</p>
            </div>
          </div>
        </div>

        <!-- Bulk Actions -->
        <div class="document-upload-modal__bulk-actions">
          <h4>Apply to All Files:</h4>
          <div class="document-upload-modal__bulk-buttons">
            <select 
              class="document-upload-modal__bulk-select"
              (change)="applyBulkCategory($any($event.target).value)">
              <option value="">Apply Category...</option>
              <option 
                *ngFor="let category of documentCategories"
                [value]="category.value">
                {{ category.icon }} {{ category.label }}
              </option>
            </select>
            
            <!-- Access levels removed for personal documents -->
          </div>
        </div>

        <!-- Individual File Configuration -->
        <div class="document-upload-modal__file-config">
          <h4>Individual File Settings:</h4>
          <div class="document-upload-modal__config-files">
            <div 
              *ngFor="let file of uploadData.files"
              class="document-upload-modal__config-file">
              
              <div class="document-upload-modal__config-header">
                <span class="document-upload-modal__config-icon">{{ getFileIcon(file) }}</span>
                <span class="document-upload-modal__config-name">{{ file.name }}</span>
                <div class="document-upload-modal__field-info">
                  {{ getCategoryOption(file.category)?.description }}
                </div>
              </div>
              
              <div class="document-upload-modal__config-controls">
                <select 
                  class="document-upload-modal__config-select"
                  [ngModel]="file.category"
                  (ngModelChange)="updateFileProperty(file.id, 'category', $event)"
                  (change)="updateFileProperty(file.id, 'category', $any($event.target).value)">
                  <option 
                    *ngFor="let category of documentCategories"
                    [value]="category.value">
                    {{ category.icon }} {{ category.label }}
                  </option>
                </select>
                
                <!-- Access level removed for personal documents -->
                
                <input 
                  type="text"
                  class="document-upload-modal__config-input"
                  placeholder="Description (optional)"
                  [value]="file.description"
                  (input)="updateFileProperty(file.id, 'description', $any($event.target).value)">
              </div>
            </div>
          </div>
        </div>

        <!-- Additional Options -->
        <div class="document-upload-modal__options">
          <div class="document-upload-modal__checkbox-group">
            <!-- Simplified personal document upload - no chapter notifications needed -->
          </div>
        </div>
      </div>
    </div>

    <!-- Step 3: Review & Upload -->
    <div *ngIf="currentStep === 3" class="document-upload-modal__step-content">
      <div class="document-upload-modal__section">
        <h3 class="document-upload-modal__section-title">👁️ Review & Upload</h3>
        
        <!-- Upload Summary -->
        <div class="document-upload-modal__upload-summary">
          <div class="document-upload-modal__summary-card">
            <div class="document-upload-modal__summary-title">📊 Upload Summary</div>
            <div class="document-upload-modal__summary-items">
              <div class="document-upload-modal__summary-item">
                <span class="document-upload-modal__summary-label">Files:</span>
                <span class="document-upload-modal__summary-value">{{ uploadData.files.length }} files</span>
              </div>
              <div class="document-upload-modal__summary-item">
                <span class="document-upload-modal__summary-label">Total Size:</span>
                <span class="document-upload-modal__summary-value">{{ formatFileSize(getTotalFileSize()) }}</span>
              </div>
              <div class="document-upload-modal__summary-item">
                <span class="document-upload-modal__summary-label">Destination:</span>
                <span class="document-upload-modal__summary-value">Personal Repository</span>
              </div>
              <div class="document-upload-modal__summary-item">
                <span class="document-upload-modal__summary-label">Est. Upload Time:</span>
                <span class="document-upload-modal__summary-value">{{ getEstimatedUploadTime() }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Files Preview -->
        <div class="document-upload-modal__files-preview">
          <h4>Files to Upload:</h4>
          <div class="document-upload-modal__preview-list">
            <div 
              *ngFor="let file of uploadData.files"
              class="document-upload-modal__preview-item">
              
              <div class="document-upload-modal__preview-icon">{{ getFileIcon(file) }}</div>
              <div class="document-upload-modal__preview-info">
                <div class="document-upload-modal__preview-name">{{ file.name }}</div>
                <div class="document-upload-modal__preview-details">
                  {{ formatFileSize(file.size) }} • 
                  {{ getCategoryOption(file.category)?.label }}
                  <span *ngIf="file.description"> • {{ file.description }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Upload Destination -->
        <div class="document-upload-modal__destination-preview">
          <h4>Upload Destination:</h4>
          <div class="document-upload-modal__destination-info">
            <span class="document-upload-modal__destination-icon">👤</span>
            <span>Personal Document Repository (Private)</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Actions -->
    <div class="document-upload-modal__actions">
      <div class="document-upload-modal__actions-left">
        <acs-button 
          *ngIf="currentStep > 1"
          variant="secondary" 
          (clicked)="previousStep()">
          Previous
        </acs-button>
      </div>

      <div class="document-upload-modal__actions-right">
        <acs-button 
          variant="text" 
          (clicked)="onModalClose()">
          Cancel
        </acs-button>

        <acs-button 
          *ngIf="currentStep < totalSteps"
          variant="primary" 
          (clicked)="nextStep()"
          [disabled]="!canProceedToStep(currentStep + 1)">
          Next
        </acs-button>

        <acs-button 
          *ngIf="currentStep === totalSteps"
          variant="primary" 
          (clicked)="uploadDocuments()"
          [disabled]="!canUpload() || isUploading"
          [loading]="isUploading">
          {{ isUploading ? 'Uploading...' : 'Upload Documents' }}
        </acs-button>
      </div>
    </div>

  </div>
</acs-modal>